/**
Author - Vikrant Singh
Git - git@github.com:vikrantSingh97
**/

import React, {useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {FlowType} from '../../../enums';
import IMAGES from 'constants/Images';
import {Spinner, ActionButton} from '../../commons';

import {ProgressBar} from 'components/ProgressBar';

const propTypes = {
  handleFormSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isPanAadhaarSeeded: PropTypes.bool.isRequired,
  isFetchingPanAadhaarSeeded: PropTypes.bool.isRequired,
};

const CheckDigiFlow = ({
  handleFormSubmit,
  isLoading,
  isPanAadhaarSeeded,
  isFetchingPanAadharSeeded,
}) => {
  const [selectedOption, setSelectedOption] = useState(FlowType.Manual);

  const loadingMessage = isFetchingPanAadharSeeded
    ? 'Checking whether your PAN is linked with Aadhaar Card, Please do not close the window'
    : 'Updating your information, Please do not close the window';

  const handleOptionSelect = (option) => {
    if (option === FlowType.Digilocker && !isPanAadhaarSeeded) {
      return;
    }
    setSelectedOption(option);
  };

  const handleContinue = () => {
    if (selectedOption) {
      handleFormSubmit(selectedOption);
    }
  };

  return isFetchingPanAadharSeeded || isLoading ? (
    <LoaderContainer>
      <ProgressBar
        waitTimeInSec={100}
        loadingTexts={[loadingMessage]}
        stopOnEndTime={true}
        stopOnEndMsg={null}
        subheading={''}
        heading="Fetching Your PAN Details"
      />
    </LoaderContainer>
  ) : (
    <Container>
      <ContentWrapper>
        <OptionsContainer>
          <OptionCard
            onClick={() => handleOptionSelect(FlowType.Manual)}
            isSelected={selectedOption === FlowType.Manual}
          >
            <RadioButton isSelected={selectedOption === FlowType.Manual}>
              {selectedOption === FlowType.Manual && <RadioButtonInner />}
            </RadioButton>

            <OptionContent>
              <OptionTextContent>
                <OptionTitle>
                  Upload PAN
                  <RecommendedTag>Recommended</RecommendedTag>
                </OptionTitle>
                <OptionDescription>
                  Upload PAN form your device. it can be pdf or image
                </OptionDescription>
              </OptionTextContent>

              <OptionIconWrapper>
                <OptionIcon src={IMAGES.uploadPanIcon} alt="Upload PAN" />
              </OptionIconWrapper>
            </OptionContent>
          </OptionCard>

          <OptionCard
            onClick={() => handleOptionSelect(FlowType.Digilocker)}
            isSelected={selectedOption === FlowType.Digilocker}
            disabled={!isPanAadhaarSeeded}
          >
            <RadioButton
              isSelected={selectedOption === FlowType.Digilocker}
              disabled={!isPanAadhaarSeeded}
            >
              {selectedOption === FlowType.Digilocker && <RadioButtonInner />}
            </RadioButton>

            <OptionContent>
              <OptionTextContent>
                <OptionTitleWrapper>
                  <OptionTitle>Digilocker OTP</OptionTitle>
                </OptionTitleWrapper>
                <OptionDescription>
                  {isPanAadhaarSeeded
                    ? 'OTP will be sent on Aadhaar linked Mobile Number'
                    : 'It seems your PAN is not linked with Aaadhaar'}
                </OptionDescription>
              </OptionTextContent>

              <OptionIconWrapper>
                <OptionIcon src={IMAGES.digiLocker} alt="Digilocker" />
              </OptionIconWrapper>
            </OptionContent>
          </OptionCard>
        </OptionsContainer>

        <ContinueButton onClick={handleContinue} disabled={!selectedOption || isLoading}>
          Continue
        </ContinueButton>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled.div`
  height: 100%;
  width: 100%;
  scroll-behavior: scroll;
  position: relative;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: ${(props) => props.theme.colors.background.primary};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;
  padding: 2rem;
  flex-grow: 1;
  height: 100%;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  margin-bottom: 2rem;
`;

const OptionCard = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 1.5rem;
  border-radius: 8px;
  background-color: ${(props) =>
    props.disabled
      ? props.theme.colors.background.primary
      : props.theme.colors.background.secondary};
  border: 1px solid
    ${(props) => {
      if (props.disabled) return props.theme.colors.border.default;
      return props.isSelected ? props.theme.colors.brand.primary : 'transparent';
    }};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    border-color: ${(props) =>
      props.disabled ? props.theme.colors.border.default : props.theme.colors.brand.primary};
  }

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const RadioButton = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${(props) => {
      if (props.disabled) return props.theme.colors.text.disabled;
      return props.isSelected ? props.theme.colors.brand.primary : props.theme.colors.text.disabled;
    }};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  margin-top: 0.5rem;
  flex-shrink: 0;
`;

const RadioButtonInner = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.brand.primary};
`;

const OptionContent = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const OptionIconWrapper = styled.div`
  margin-right: 1rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
`;

const OptionIcon = styled.img`
  width: 64px;
  height: 64px;
  object-fit: contain;
  flex-shrink: 0;
`;

const OptionTextContent = styled.div`
  flex: 1;
`;

const OptionTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const OptionTitle = styled.h3`
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;

  @media (max-width: 768px) {
    font-size: ${(props) => props.theme.typography.fontSize.xs};
  }
`;

const RecommendedTag = styled.span`
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  font-size: ${(props) => props.theme.typography.fontSize.xs};
  color: ${(props) => props.theme.colors.feedback.success};
  border: 1px solid ${(props) => props.theme.colors.feedback.success};
  border-radius: 1rem;
  padding: 0.2rem 0.5rem;
  margin-left: 0.75rem;
  display: inline-block;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 0.625rem;
    padding: 0.125rem 0.375rem;
    margin-left: 0.5rem;
    border-radius: 0.75rem;
  }

  @media (max-width: 480px) {
    font-size: 0.5rem;
    padding: 0.1rem 0.3rem;
    margin-left: 0.375rem;
    border-radius: 0.5rem;
  }
`;

const OptionDescription = styled.p`
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  font-size: ${(props) => props.theme.typography.fontSize.xs};
  font-weight: ${(props) => props.theme.typography.fontWeight.regular};
  color: ${(props) => props.theme.colors.text.secondary};
  margin: 0 0 1rem 0;

  @media (max-width: 768px) {
    font-size: ${(props) => props.theme.typography.fontSize.xxs};
  }
`;

const ContinueButton = styled.button`
  width: 100%;
  padding: 1rem;
  background-color: ${(props) =>
    props.disabled ? props.theme.colors.text.disabled : props.theme.colors.brand.primary};
  color: white;
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  font-size: ${(props) => props.theme.typography.fontSize.sm};
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  border: none;
  border-radius: 2rem;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  margin-top: auto;
`;

const LoaderContainer = styled.div`
  height: 80vh;
  display: flex;
  justify-content: center;
  align-items: center;
  ${'' /* background: #F8F4FF; */}

  ${'' /* height: 100vh; */}
  width: 100%;
  scroll-behavior: scroll;
  position: relative;
  overflow-y: scroll;
  border-radius: 2rem;

  img {
    width: 25px;
    height: unset;
  }
`;

CheckDigiFlow.propTypes = propTypes;

export default CheckDigiFlow;
